import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #isMobile = false;
  /** @type {HTMLElement | null} */
  #clonedSlides = null;
  #isInitialized = false;

  connectedCallback() {
    super.connectedCallback();

    this.#initializeMarquee();
    
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  #checkMobile() {
    this.#isMobile = window.innerWidth < 990;
  }

  #handleResize = debounce(() => {
    const wasMobile = this.#isMobile;
    this.#checkMobile();
    
    if (wasMobile !== this.#isMobile) {
      this.#setupMarquee();
    } else if (this.#isMobile && this.classList.contains('announcement-bar--marquee')) {
      const slidesContainer = /** @type {HTMLElement} */ (this.querySelector('.announcement-bar__slides'));
      if (slidesContainer) {
        this.#setMarqueeSpeed(slidesContainer);
      }
    }
  }, 250);

  #initializeMarquee() {
    this.#checkMobile();
    this.#setupMarquee();
    this.#isInitialized = true;
    
    requestAnimationFrame(() => {
      this.classList.remove('announcement-bar--loading');
    });
  }

  #setupMarquee() {
    const marqueeWrapper = /** @type {HTMLElement} */ (this.querySelector('.announcement-bar__marquee-wrapper'));
    const slidesContainer = /** @type {HTMLElement} */ (this.querySelector('.announcement-bar__slides'));
    if (!marqueeWrapper || !slidesContainer) return;

    if (this.#isMobile) {
      this.#enableMarquee(marqueeWrapper, slidesContainer);
    } else {
      this.#disableMarquee(marqueeWrapper);
    }
  }

  /**
   * @param {HTMLElement} marqueeWrapper
   * @param {HTMLElement} slidesContainer
   */
  #enableMarquee(marqueeWrapper, slidesContainer) {
    this.#removeClonedSlides();
    
    this.#clonedSlides = /** @type {HTMLElement} */ (slidesContainer.cloneNode(true));
    this.#clonedSlides.setAttribute('aria-hidden', 'true');
    this.#clonedSlides.classList.add('announcement-bar__slides--cloned');
    
    marqueeWrapper.appendChild(this.#clonedSlides);
    
    this.classList.add('announcement-bar--marquee');
    this.#setMarqueeSpeed(slidesContainer);
  }

  /**
   * @param {HTMLElement} marqueeWrapper
   */
  #disableMarquee(marqueeWrapper) {
    this.#removeClonedSlides();
    this.classList.remove('announcement-bar--marquee');
  }

  #removeClonedSlides() {
    if (this.#clonedSlides) {
      this.#clonedSlides.remove();
      this.#clonedSlides = null;
    }
  }

  /**
   * @param {HTMLElement} slidesContainer
   */
  #setMarqueeSpeed(slidesContainer) {
    requestAnimationFrame(() => {
      const containerWidth = slidesContainer.offsetWidth;
      
      const computedStyle = getComputedStyle(this);
      const gapValue = computedStyle.getPropertyValue('--announcement-gap').trim();
      const gap = parseInt(gapValue) || (window.innerWidth >= 768 ? 65 : 35);
      
      const speed = Math.max(containerWidth / 20, 15);
      
      this.style.setProperty('--marquee-distance', `${containerWidth + gap}px`);
      this.style.setProperty('--announcement-marquee-speed', `${speed}s`);
    });
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
